/**
 * Insurance Service - Handles CRUD operations for insurance policies using Firestore
 *
 * ENCRYPTION: Policy numbers, group numbers, and member IDs are encrypted using AES-256-GCM
 * before being stored in Firestore. Data is decrypted when retrieved.
 */

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { InsurancePolicy } from '../types/insurance';
import { encryptionService } from './encryptionService';
import { ProfileService } from './profileService';

/**
 * Check if user is authenticated with Firebase
 * Returns true if there's a current Firebase user AND localStorage indicates authenticated
 */
function isAuthenticated(): boolean {
  const hasFirebaseUser = !!auth.currentUser;
  const hasLocalAuth = localStorage.getItem('isAuthenticated') === 'true';
  return hasFirebaseUser && hasLocalAuth;
}

/** Recursively strip undefined values — Firestore rejects undefined at any depth. */
function removeUndefined(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, removeUndefined(v)])
    );
  }
  return obj;
}

/** Returns the subcollection ref for a profile's insurance policies. */
function insuranceCollection(profileId: string) {
  return collection(db, 'profiles', profileId, 'insurance');
}

/** Returns a doc ref within the insurance subcollection. */
function insuranceDoc(profileId: string, docId: string) {
  return doc(db, 'profiles', profileId, 'insurance', docId);
}

// Fields to encrypt in insurance policies
const ENCRYPTED_FIELDS = ['policyNumber', 'groupNumber', 'memberId'] as const;

class InsuranceService {
  private cachedPolicies: Map<string, InsurancePolicy[]> = new Map();
  private unsubscribes: Map<string, Unsubscribe> = new Map();
  private subscriberCallbacks: Map<string, (policies: InsurancePolicy[]) => void> = new Map();
  /**
   * Monotonic counter incremented on every local mutation (create/update/delete)
   * AND on every onSnapshot callback start. After async decryption, the snapshot
   * callback checks whether a newer mutation or snapshot has arrived — if so, it
   * discards its stale result. This prevents the race where a CREATE's onSnapshot
   * (with no portals) overwrites a subsequent UPDATE's cache (with portals).
   */
  private _dataVersion = 0;
  /**
   * Serializes ALL Firestore writes per document ID. Every write (create, update)
   * chains after the previous write for the same document, preventing races where
   * two concurrent updateDoc calls overwrite each other (e.g. agent sync vs curated portals).
   */
  private _pendingWrites = new Map<string, Promise<void>>();

  /** Notify active subscriber with current cache (instant UI update after mutations) */
  private notifySubscribers(profileId: string) {
    const callback = this.subscriberCallbacks.get(profileId);
    const cached = this.cachedPolicies.get(profileId) || [];
    if (callback) callback([...cached]);
  }

  /**
   * Gets the encryption user ID from ProfileService
   * Uses profile owner ID for consistency across all data access
   */
  private getCurrentUserId(): string {
    return ProfileService.getEncryptionUserId();
  }

  /**
   * Encrypts sensitive fields in a policy before storing
   */
  private async encryptPolicy(policy: InsurancePolicy | Omit<InsurancePolicy, 'id' | 'createdAt' | 'updatedAt'>, userId: string): Promise<any> {
    const encrypted: any = { ...policy };

    for (const field of ENCRYPTED_FIELDS) {
      if (encrypted[field]) {
        encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
      }
    }

    return encrypted;
  }

  /**
   * Decrypts sensitive fields in a policy after retrieval
   */
  private async decryptPolicy(policy: InsurancePolicy, userId: string): Promise<InsurancePolicy> {
    const decrypted: any = { ...policy };

    for (const field of ENCRYPTED_FIELDS) {
      if (decrypted[field]) {
        decrypted[field] = await encryptionService.decrypt(decrypted[field], userId);
      }
    }

    return decrypted as InsurancePolicy;
  }

  /**
   * Encrypts only the fields that are being updated
   */
  private async encryptUpdates(updates: Partial<InsurancePolicy>, userId: string): Promise<Partial<InsurancePolicy>> {
    const encrypted: any = { ...updates };

    for (const field of ENCRYPTED_FIELDS) {
      if (field in encrypted && encrypted[field]) {
        encrypted[field] = await encryptionService.encrypt(encrypted[field], userId);
      }
    }

    return encrypted;
  }

  /**
   * Pre-fetch insurance policies so data is cached before page mounts.
   * Called during app initialization in ProfileContext.
   */
  async prefetch(profileId: string): Promise<void> {
    if (!isAuthenticated()) return;
    if (this.cachedPolicies.has(profileId)) return; // Already cached
    await this.listAsync(profileId);
  }

  // Subscribe to real-time updates for a profile's insurance policies
  subscribeToPolicies(profileId: string, callback: (policies: InsurancePolicy[]) => void): Unsubscribe {
    // CRITICAL: Check authentication before making Firestore calls
    // This prevents permission errors when user is not logged in (e.g., Private Guest Mode)
    if (!isAuthenticated()) {
      // Call callback with empty array so loading state clears
      setTimeout(() => callback([]), 0);
      return () => {}; // Return no-op unsubscribe
    }

    // Store callback for instant notification on cache mutations
    this.subscriberCallbacks.set(profileId, callback);

    // Immediately return pre-fetched data if available (instant load)
    // Includes empty arrays — so empty state shows instantly after prefetch
    const prefetched = this.cachedPolicies.get(profileId);
    if (prefetched) {
      setTimeout(() => callback(prefetched), 0);
    }

    // Unsubscribe from previous listener if exists
    const existingUnsub = this.unsubscribes.get(profileId);
    if (existingUnsub) {
      existingUnsub();
    }

    const userId = this.getCurrentUserId();

    const unsubscribe = onSnapshot(insuranceCollection(profileId), async (snapshot) => {
      // Capture a version token BEFORE the async decryption. If any local
      // mutation (create/update/delete) fires while we're awaiting, it bumps
      // _dataVersion, and we discard this now-stale Firestore snapshot.
      const myVersion = ++this._dataVersion;

      const policies: InsurancePolicy[] = [];
      snapshot.forEach((docSnap) => {
        policies.push({ id: docSnap.id, ...docSnap.data() } as InsurancePolicy);
      });

      // Decrypt policies
      const decryptedPolicies = await Promise.all(
        policies.map(policy => this.decryptPolicy(policy, userId))
      );

      // A local mutation happened during decryption — skip this stale snapshot.
      // The mutation already updated the cache and notified subscribers.
      if (this._dataVersion !== myVersion) return;

      // Sort by company name
      decryptedPolicies.sort((a, b) => a.company.localeCompare(b.company));
      this.cachedPolicies.set(profileId, decryptedPolicies);
      callback(decryptedPolicies);
    }, (error) => {
      // Handle permission errors - call callback with cached data or empty array
      // This ensures loading state clears even on errors
      console.warn('Insurance subscription error:', error);
      const cached = this.cachedPolicies.get(profileId) || [];
      callback(cached);
    });

    this.unsubscribes.set(profileId, unsubscribe);
    return unsubscribe;
  }

  // Get all insurance policies for a profile (async, from Firestore)
  async listAsync(profileId: string): Promise<InsurancePolicy[]> {
    // CRITICAL: Check authentication before making Firestore calls
    if (!isAuthenticated()) {
      return [];
    }

    try {
      const snapshot = await getDocs(insuranceCollection(profileId));
      const userId = this.getCurrentUserId();

      const policies: InsurancePolicy[] = [];
      snapshot.forEach((docSnap) => {
        policies.push({ id: docSnap.id, ...docSnap.data() } as InsurancePolicy);
      });

      // Decrypt policies
      const decryptedPolicies = await Promise.all(
        policies.map(policy => this.decryptPolicy(policy, userId))
      );

      // Sort by company name
      decryptedPolicies.sort((a, b) => a.company.localeCompare(b.company));
      this.cachedPolicies.set(profileId, decryptedPolicies);
      return decryptedPolicies;
    } catch (error) {
      console.error('Error loading insurance policies:', error);
      return [];
    }
  }

  // Get all insurance policies (sync, from cache - for backwards compatibility)
  list(profileId: string): InsurancePolicy[] {
    return this.cachedPolicies.get(profileId) || [];
  }

  // Create new insurance policy
  create(profileId: string, policy: Omit<InsurancePolicy, 'id' | 'createdAt' | 'updatedAt'>): InsurancePolicy {
    // Check for duplicate insurance policy (same company and type)
    const cached = this.cachedPolicies.get(profileId) || [];
    const isDuplicate = cached.some(existingPolicy => {
      return (
        existingPolicy.company.toLowerCase().trim() === policy.company.toLowerCase().trim() &&
        existingPolicy.insuranceType === policy.insuranceType
      );
    });

    if (isDuplicate) {
      throw new Error('This insurance policy already exists in your profile');
    }

    const id = `ins_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const now = new Date().toISOString();
    const userId = this.getCurrentUserId();

    const newPolicy: InsurancePolicy = {
      ...policy,
      id,
      createdAt: now,
    };

    // If this is primary, unset others
    if (newPolicy.isPrimary) {
      cached.forEach(p => {
        if (p.isPrimary) {
          updateDoc(insuranceDoc(profileId, p.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary policy:', error);
          });
        }
      });
    }

    // Encrypt and save to Firestore (async, but we return immediately for responsive UI).
    // Serialize via _pendingWrites so subsequent update() calls wait for this to complete.
    const prev = this._pendingWrites.get(id) || Promise.resolve();
    const writePromise = prev.then(() =>
      this.encryptPolicy(newPolicy, userId).then(encryptedPolicy => {
        const cleanPolicy = removeUndefined(encryptedPolicy) as Record<string, unknown>;
        return setDoc(insuranceDoc(profileId, id), cleanPolicy);
      })
    ).catch(error => {
      console.error('Error saving insurance policy to Firestore:', error);
    });
    this._pendingWrites.set(id, writePromise);

    // Update cache immediately for responsive UI (with unencrypted data for display)
    this._dataVersion++;
    if (newPolicy.isPrimary) {
      cached.forEach(p => (p.isPrimary = false));
    }
    cached.push(newPolicy);
    cached.sort((a, b) => a.company.localeCompare(b.company));
    this.cachedPolicies.set(profileId, cached);
    this.notifySubscribers(profileId);

    return newPolicy;
  }

  // Update insurance policy
  update(profileId: string, id: string, updates: Partial<InsurancePolicy>): InsurancePolicy {
    const cached = this.cachedPolicies.get(profileId) || [];
    const index = cached.findIndex(p => p.id === id);
    if (index === -1) throw new Error('Policy not found in cache');

    const now = new Date().toISOString();
    const userId = this.getCurrentUserId();
    const updatedPolicy = { ...cached[index], ...updates, updatedAt: now };

    // Check for duplicate (excluding current policy being updated)
    if (updates.company || updates.insuranceType) {
      const isDuplicate = cached.some((existingPolicy, idx) => {
        if (idx === index) return false; // Skip the current policy
        return (
          existingPolicy.company.toLowerCase().trim() === updatedPolicy.company.toLowerCase().trim() &&
          existingPolicy.insuranceType === updatedPolicy.insuranceType
        );
      });

      if (isDuplicate) {
        throw new Error('This insurance policy already exists in your profile');
      }
    }

    // If setting as primary, unset others
    if (updates.isPrimary) {
      cached.forEach(p => {
        if (p.id !== id && p.isPrimary) {
          p.isPrimary = false;
          updateDoc(insuranceDoc(profileId, p.id), { isPrimary: false }).catch(error => {
            console.error('Error unsetting primary policy:', error);
          });
        }
      });
    }

    // Update cache immediately (with unencrypted data for display)
    this._dataVersion++;
    cached[index] = updatedPolicy;
    this.cachedPolicies.set(profileId, cached);
    this.notifySubscribers(profileId);

    // Encrypt and save to Firestore (async).
    // Serialize via _pendingWrites — waits for any prior create or update on this doc.
    const prev = this._pendingWrites.get(id) || Promise.resolve();
    const writePromise = prev.then(() =>
      this.encryptUpdates(updates, userId).then(encryptedUpdates => {
        const cleanUpdates: Record<string, unknown> = { updatedAt: now };
        for (const [key, value] of Object.entries(encryptedUpdates)) {
          if (value !== undefined) {
            cleanUpdates[key] = removeUndefined(value) as unknown;
          }
        }
        return updateDoc(insuranceDoc(profileId, id), cleanUpdates);
      })
    ).catch(error => {
      console.error('Error updating insurance policy in Firestore:', error);
    });
    this._pendingWrites.set(id, writePromise);

    return updatedPolicy;
  }

  // Update insurance policy (async version - tries Firestore first, more reliable)
  async updateAsync(profileId: string, id: string, updates: Partial<InsurancePolicy>): Promise<InsurancePolicy | null> {
    const now = new Date().toISOString();
    const userId = this.getCurrentUserId();
    const policyRef = insuranceDoc(profileId, id);

    // Encrypt sensitive fields before saving
    const encryptedUpdates = await this.encryptUpdates(updates, userId);

    // Filter out undefined values (deep) - Firestore doesn't accept undefined at any depth
    const cleanUpdates: Record<string, unknown> = { updatedAt: now };
    for (const [key, value] of Object.entries(encryptedUpdates)) {
      if (value !== undefined) {
        cleanUpdates[key] = removeUndefined(value) as unknown;
      }
    }

    try {
      // Check if document exists first
      const docSnap = await getDoc(policyRef);

      if (docSnap.exists()) {
        // Document exists, use updateDoc
        await updateDoc(policyRef, cleanUpdates);
      } else {
        // Document doesn't exist - get policy from cache and create it
        const cached = this.cachedPolicies.get(profileId) || [];
        const existingPolicy = cached.find(p => p.id === id);

        if (existingPolicy) {
          // Encrypt the existing policy before saving
          const encryptedExisting = await this.encryptPolicy(existingPolicy, userId);
          const cleanExisting = removeUndefined(encryptedExisting) as Record<string, unknown>;
          // Create the document with existing policy data + updates
          await setDoc(policyRef, {
            ...cleanExisting,
            ...cleanUpdates,
          });
        } else {
          // No cached data, can't create - throw to fallback
          throw new Error('Policy not found in Firestore collection or cache');
        }
      }

      // Update cache if policy exists there (with unencrypted data for display)
      const cached = this.cachedPolicies.get(profileId) || [];
      const index = cached.findIndex(p => p.id === id);
      if (index !== -1) {
        this._dataVersion++;
        cached[index] = { ...cached[index], ...updates, updatedAt: now };
        this.cachedPolicies.set(profileId, cached);
        this.notifySubscribers(profileId);
        return cached[index];
      }
      return null;
    } catch (error) {
      // Document doesn't exist in the insurance collection - throw to allow fallback
      throw new Error('Policy not found in Firestore collection');
    }
  }

  // Delete insurance policy
  async delete(profileId: string, id: string): Promise<boolean> {
    try {
      // Update cache immediately for responsive UI
      this._dataVersion++;
      const cached = this.cachedPolicies.get(profileId) || [];
      this.cachedPolicies.set(profileId, cached.filter(p => p.id !== id));
      this.notifySubscribers(profileId);

      // Delete from Firestore
      await deleteDoc(insuranceDoc(profileId, id));
      return true;
    } catch (error) {
      console.error('Error deleting insurance policy from Firestore:', error);
      // Rollback cache on error - restore the policy
      const cachedPolicies = await this.listAsync(profileId);
      this.cachedPolicies.set(profileId, cachedPolicies);
      throw error;
    }
  }

  // Cleanup subscriptions
  cleanup(profileId?: string): void {
    if (profileId) {
      const unsub = this.unsubscribes.get(profileId);
      if (unsub) {
        unsub();
        this.unsubscribes.delete(profileId);
      }
      this.cachedPolicies.delete(profileId);
      this.subscriberCallbacks.delete(profileId);
    } else {
      // Cleanup all
      this.unsubscribes.forEach(unsub => unsub());
      this.unsubscribes.clear();
      this.cachedPolicies.clear();
      this.subscriberCallbacks.clear();
    }
  }
}

export const insuranceService = new InsuranceService();
